import { useEffect, useState } from "react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Key,
  Mail,
  MapPin,
  Phone,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { db } from "../db.ts";
import { api } from "../api.ts";
import { Spinner } from "../components/Spinner.tsx";
import { PhoneVerification } from "../components/PhoneVerification.tsx";
import { WhatsAppLogo } from "../components/EventCard.tsx";

interface UserProfileData {
  userId: string;
  email?: string;
  location?: string;
  city?: string;
  country?: string;
  coordinates?: [number, number] | null;
  phone?: string;
  phoneVerified?: boolean;
  isPremium?: boolean;
}

export const SettingsPageContent = () => {
  const { user } = db.useAuth();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiToken, setApiToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [isChangingPhone, setIsChangingPhone] = useState(false);

  const { data: instantUserData, isLoading: isInstantUserLoading } = db
    .useQuery(
      user?.id
        ? {
          $users: {
            $: { where: { id: user.id } },
          },
        }
        : null,
    );

  const instantUser = instantUserData?.$users?.[0];

  const fetchProfile = () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    api({
      endpoint: "getUserProfile",
      payload: {},
    })
      .then((data) => {
        setProfile(data);
      })
      .catch((err: unknown) => {
        console.error("Failed to load user profile:", err);
        setError(err instanceof Error ? err.message : "Failed to load profile");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchProfile();
  }, [user?.id]);

  const handleFetchToken = async () => {
    setLoadingToken(true);
    try {
      const res = await api({
        endpoint: "getApiToken",
        payload: {},
      });
      setApiToken(res.token);
    } catch (err) {
      console.error("Failed to get API token:", err);
    } finally {
      setLoadingToken(false);
    }
  };

  const handleCopyToken = () => {
    if (!apiToken) return;
    navigator.clipboard.writeText(apiToken);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  if (loading || isInstantUserLoading) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-16 flex justify-center items-center">
        <Spinner className="h-8 w-8 text-purple-500" />
      </div>
    );
  }

  const effectivePhone = profile?.phone || instantUser?.phone;
  const isPhoneVerified = Boolean(
    profile?.phoneVerified ||
      instantUser?.phoneVerified ||
      (effectivePhone && effectivePhone.length >= 7),
  );

  return (
    <div className="container mx-auto max-w-2xl px-4 py-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight flex items-center gap-2.5">
          <span>Settings</span>
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Manage your account, phone number, and preferences
        </p>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Account Info Card */}
      <div className="glass-card p-6 rounded-2xl border border-white/10 space-y-5">
        <h2 className="text-base font-bold text-white uppercase tracking-wider text-xs text-purple-400">
          Account Profile
        </h2>

        <div className="space-y-4">
          {/* Email */}
          <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/15 border border-purple-500/25 flex items-center justify-center text-purple-300">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">
                  Email Address
                </p>
                <p className="text-sm font-semibold text-white font-mono">
                  {user?.email || "No email"}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Verified
            </span>
          </div>

          {/* Associated Phone Number */}
          <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center text-emerald-300">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">
                  Associated Phone Number
                </p>
                <p className="text-sm font-semibold text-white font-mono">
                  {effectivePhone || "No phone number linked"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {effectivePhone && isPhoneVerified
                ? (
                  <>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Verified
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsChangingPhone(!isChangingPhone)}
                      className="text-xs text-gray-400 hover:text-white px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition"
                    >
                      {isChangingPhone ? "Cancel" : "Change"}
                    </button>
                  </>
                )
                : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full">
                    Unverified
                  </span>
                )}
            </div>
          </div>
        </div>

        {/* Change / Add Phone Section */}
        {(!effectivePhone || isChangingPhone) && (
          <div className="pt-3 border-t border-white/5">
            <p className="text-xs text-gray-400 mb-3">
              {effectivePhone
                ? "Verify a new phone number to update your associated phone."
                : "Link your phone number via SMS to unlock live event recommendations, WhatsApp digests, and ticket trades."}
            </p>
            <PhoneVerification
              embedded
              onVerified={() => {
                setIsChangingPhone(false);
                fetchProfile();
              }}
            />
          </div>
        )}
      </div>

      {/* Subscription & Plan Card */}
      <div className="glass-card p-6 rounded-2xl border border-white/10 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <h2 className="text-base font-bold text-white uppercase tracking-wider text-xs text-purple-400">
              Subscription & Plan
            </h2>
          </div>
          {profile?.isPremium
            ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Premium Active
              </span>
            )
            : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                Free Tier
              </span>
            )}
        </div>

        {profile?.isPremium
          ? (
            <p className="text-sm text-gray-300 leading-relaxed font-light">
              You are an <strong>Agent FOMO Premium</strong>{" "}
              member. You have unlimited WhatsApp conversations, event
              recommendations, and alerts.
            </p>
          )
          : (
            <div className="space-y-3">
              <p className="text-sm text-gray-300 leading-relaxed font-light">
                Free accounts are limited to 30 messages and 20 event
                recommendations per month. Upgrade to Premium for unlimited
                access across WhatsApp and the web.
              </p>
              <div>
                <Link
                  to={effectivePhone
                    ? `/upgrade?phone=${encodeURIComponent(effectivePhone)}`
                    : "/upgrade"}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs font-semibold shadow-md shadow-purple-600/20 transition active:scale-95"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Upgrade to Premium</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          )}
      </div>

      {/* WhatsApp Agent Pairing Card */}
      <div className="glass-card p-6 rounded-2xl border border-white/10 space-y-4">
        <div className="flex items-center gap-2.5">
          <WhatsAppLogo className="w-5 h-5 text-emerald-400" />
          <h2 className="text-base font-bold text-white uppercase tracking-wider text-xs text-emerald-400">
            WhatsApp Agent Pairing
          </h2>
        </div>
        <p className="text-gray-300 text-sm leading-relaxed">
          Message <strong>Agent FOMO</strong>{" "}
          directly on WhatsApp to get tailored recommendations, notifications
          for secret events, and taste alerts in chat.
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <a
            href={`https://wa.me/972504244206?text=My%20email%20is%20${
              encodeURIComponent(user?.email || "")
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/20 transition"
          >
            <WhatsAppLogo className="w-4 h-4" />
            <span>Open WhatsApp Chat</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Location Preferences */}
      {(profile?.city || profile?.country || profile?.location) && (
        <div className="glass-card p-6 rounded-2xl border border-white/10 space-y-3">
          <h2 className="text-base font-bold text-white uppercase tracking-wider text-xs text-purple-400 flex items-center gap-1.5">
            <MapPin className="w-4 h-4" />
            <span>Location & Region</span>
          </h2>
          <div className="flex flex-wrap gap-2 text-sm text-gray-300">
            {profile.city && (
              <span className="px-3 py-1 rounded-lg bg-white/5 border border-white/10">
                City: <strong className="text-white">{profile.city}</strong>
              </span>
            )}
            {profile.country && (
              <span className="px-3 py-1 rounded-lg bg-white/5 border border-white/10">
                Country:{" "}
                <strong className="text-white">{profile.country}</strong>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Developer API Token Card */}
      <div className="glass-card p-6 rounded-2xl border border-white/10 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-purple-400" />
            <h2 className="text-base font-bold text-white uppercase tracking-wider text-xs text-purple-400">
              Developer API Access
            </h2>
          </div>
          <a
            href="/docs"
            className="text-xs font-semibold text-purple-400 hover:text-purple-300 flex items-center gap-1"
          >
            API Docs <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <p className="text-gray-400 text-xs leading-relaxed">
          Use this personal bearer token to query the Agent FOMO API
          programmatically.
        </p>

        {apiToken
          ? (
            <div className="flex items-center gap-2 bg-black/40 p-2.5 rounded-xl border border-white/10 font-mono text-xs text-purple-200">
              <span className="truncate flex-1">{apiToken}</span>
              <button
                type="button"
                onClick={handleCopyToken}
                className="p-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white transition shrink-0"
                title="Copy Token"
              >
                {copiedToken
                  ? <Check className="w-4 h-4" />
                  : <Copy className="w-4 h-4" />}
              </button>
            </div>
          )
          : (
            <button
              type="button"
              onClick={handleFetchToken}
              disabled={loadingToken}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold text-gray-200 transition"
            >
              {loadingToken
                ? <Spinner className="w-3.5 h-3.5 text-purple-400" />
                : <Key className="w-3.5 h-3.5 text-purple-400" />}
              <span>Generate / Reveal API Token</span>
            </button>
          )}
      </div>
    </div>
  );
};
